// "My Notes" — the user's private notebook (user_note chunks). Reads and
// deletes go through the Supabase client: phase1's RLS policies
// (user_chunks_select_own / user_chunks_delete_own) scope every query to
// the caller's own rows — that IS the server-side check.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { equipmentTitle } from '../chat/ChatView';

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB') : '';
}

export default function MyNotes() {
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!supabase) return;
    const { data, error: e } = await supabase
      .from('user_chunks')
      .select('id, equipment_path, content, created_at')
      .eq('source_type', 'user_note')
      .eq('layer', 'private')
      .order('created_at', { ascending: false })
      .limit(100);
    if (e) setError(e.message);
    else setNotes(data || []);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function remove(id) {
    setBusyId(id);
    setError(null);
    try {
      const { error: e } = await supabase.from('user_chunks').delete().eq('id', id);
      if (e) throw e;
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (!notes && !error) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <div className="section-label">MY NOTES ({notes?.length || 0})</div>
      {error && (
        <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 10 }}>{error}</div>
      )}
      {notes && notes.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '4px 0 10px' }}>
          No notes yet — add one from any chat ("Add a note from experience"). Shared notes earn +20
          messages when approved.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(notes || []).map(n => (
          <div
            key={n.id}
            style={{
              background: 'var(--surface-1, #ffffff)',
              border: '1px solid var(--border-soft, #eceff6)',
              borderRadius: 14,
              padding: '12px 14px',
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word' }}>
                {n.content}
              </div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  letterSpacing: 0.5,
                  color: 'var(--text-3)',
                  marginTop: 6,
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span>📝 {equipmentTitle(n.equipment_path).toUpperCase()}</span>
                <span>{formatDate(n.created_at)}</span>
              </div>
            </div>
            <button
              className="btn btn-ghost"
              disabled={busyId === n.id}
              onClick={() => remove(n.id)}
              style={{
                padding: '7px 14px',
                fontSize: 11.5,
                borderColor: 'rgba(248,113,113,0.5)',
                color: 'var(--red)',
              }}
            >
              {busyId === n.id ? '…' : '🗑 Delete'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
